import React, { useCallback, useEffect, useMemo, useState } from 'react';

import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { Box, Typography } from '@mui/material';

import SettingsPageHeader from 'components/SettingsPageHeader';
import Circular from 'components/common/CustomCircular';
import DeleteConfirmDialog from 'components/dialog/DeleteConfirmDialog';
import {
  TicketScope,
  TicketTypeSummaryResult,
  activeTicketType,
  deleteTicketType,
  listTicketType,
} from 'stores/TicketTypeStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import TicketTypeCreateDialog from './TicketTypeCreateDialog';
import TicketTypeEditDialog from './TicketTypeEditDialog';
import TicketTypeFormEditor from './TicketTypeFormEditor';
import TicketTypeSelector from './TicketTypeSelector';

const ACCENT = '#c2185b';

type Props = {
  /** 対象スコープ */
  scope: TicketScope;
  /** グローバル操作が可能か（ADMIN/BACK_OFFICE）。プライベートは本人所有のため常に可 */
  canManageGlobal: boolean;
};

/**
 * チケット種別設定画面（グローバル / プライベート共通・scope で切替）。
 * 申請フォーム設定（TemplateManagement）と同一レイアウト:
 * SettingsPageHeader ＋ 左5(種別セレクタ＋フォームツリー) ／ 右7(編集・プレビュータブ)。
 * 左ペイン上部の種別セレクタで対象設定（種別）を切り替える。
 */
const TicketTypeSettings: React.FC<Props> = ({ scope, canManageGlobal }) => {
  const isGlobal = scope === 'GLOBAL';
  // 操作可否: グローバルは ADMIN/BACK_OFFICE のみ、プライベートは本人所有のため常に可
  const canOperate = isGlobal ? canManageGlobal : true;

  const [types, setTypes] = useState<TicketTypeSummaryResult[]>([]);
  const [progress, setProgress] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TicketTypeSummaryResult | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TicketTypeSummaryResult | null>(null);

  const fetchTypes = useCallback(
    (keepSelection = true) => {
      setProgress(true);
      listTicketType({})
        .then((all) => {
          const scoped = all.filter((t) => t.scope === scope);
          setTypes(scoped);
          setSelectedId((prev) => {
            if (keepSelection && prev && scoped.some((t) => t.typeId === prev)) return prev;
            return scoped[0]?.typeId ?? null;
          });
          setProgress(false);
        })
        .catch(() => {
          setProgress(false);
          errorDefault('設定の取得に失敗しました');
        });
    },
    [scope],
  );

  useEffect(() => {
    setSelectedId(null);
    fetchTypes(false);
  }, [fetchTypes]);

  const selected = useMemo(
    () => types.find((t) => t.typeId === selectedId) ?? null,
    [types, selectedId],
  );

  const label = isGlobal ? 'グローバルチケット設定' : 'プライベートチケット設定';
  const addLabel = `${label}の追加`;

  const handleActivate = (t: TicketTypeSummaryResult, active: boolean) => {
    activeTicketType({ typeId: t.typeId, active })
      .then(() => {
        successDefault(active ? '有効化しました' : '無効化しました');
        fetchTypes();
      })
      .catch((e) => errorDefault('操作に失敗しました', toErrorDetail(e)));
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteTicketType({ typeId: deleteTarget.typeId })
      .then(() => {
        successDefault('削除しました');
        setDeleteTarget(null);
        fetchTypes(false);
      })
      .catch((e) => {
        errorDefault('削除に失敗しました', toErrorDetail(e));
        setDeleteTarget(null);
      });
  };

  // 左ペイン上部に差し込む種別セレクタ
  const selectorSlot = (
    <TicketTypeSelector
      types={types}
      selectedId={selectedId}
      onSelect={setSelectedId}
      onAdd={() => setCreateOpen(true)}
      onActivate={(t) => handleActivate(t, true)}
      onDeactivate={(t) => handleActivate(t, false)}
      onDelete={(t) => setDeleteTarget(t)}
      onRename={(t) => setEditTarget(t)}
      canOperate={canOperate}
      addLabel={addLabel}
    />
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SettingsPageHeader
        breadcrumbs={[{ label: 'チケット管理', to: '/ticket' }, { label }]}
        icon={isGlobal ? <PublicIcon /> : <LockIcon />}
        title={label}
        description={
          isGlobal
            ? 'チーム共有のチケット設定と入力フォームを管理します'
            : '自分専用のチケット設定と入力フォームを管理します'
        }
        color={ACCENT}
      />

      {/* 申請フォーム設定と同一の 2ペイン。種別セレクタは左ペイン上部（headerSlot）に差し込む。 */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <TicketTypeFormEditor
          key={selected ? selected.typeId : `empty-${scope}`}
          typeId={selected?.typeId ?? null}
          isActive={selected?.isActive ?? false}
          canEdit={canOperate}
          headerSlot={selectorSlot}
          emptyText={
            isGlobal
              ? 'グローバルチケット設定がありません。上部の「追加」から作成してください。'
              : 'プライベートチケット設定がありません。上部の「追加」から作成してください。'
          }
        />
      </Box>

      <TicketTypeCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(typeId) => {
          fetchTypes(false);
          setSelectedId(typeId);
        }}
        canCreateGlobal={canManageGlobal}
        fixedScope={scope}
      />

      {editTarget && (
        <TicketTypeEditDialog
          open={editTarget !== null}
          onClose={() => setEditTarget(null)}
          onUpdated={fetchTypes}
          type={editTarget}
        />
      )}

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`${label}の削除`}
        confirmLabel="削除"
      >
        <Typography variant="body2">
          「{deleteTarget?.name}
          」を削除します。配下のフォームも削除され、この操作は取り消せません。
        </Typography>
      </DeleteConfirmDialog>

      <Circular open={progress} />
    </Box>
  );
};

export default TicketTypeSettings;
